import { useMemo, useState } from "react";
import { hotResources, type ResourceItem } from "@/mocks/resources";

const formatTabs = ["全部", "网盘", "直链", "磁力", "秒传"] as const;

const statusStyle: Record<string, string> = {
  热门: "bg-primary-100 text-primary-700",
  更新: "bg-accent-100 text-accent-700",
  整理: "bg-secondary-100 text-secondary-900",
  补档: "bg-foreground-100 text-foreground-700",
};

const formatStyle: Record<string, string> = {
  网盘: "text-primary-700",
  直链: "text-accent-700",
  磁力: "text-secondary-900",
  秒传: "text-accent-700",
};

function ResourceCard({ r, copiedId, onCopy }: { r: ResourceItem; copiedId: number | null; onCopy: (id: number) => void }) {
  return (
    <article className="group flex flex-col overflow-hidden rounded-lg border border-background-200/80 bg-background-50 transition-colors hover:border-primary-300">
      <div className="relative h-40 w-full overflow-hidden sm:h-44">
        <img
          src={r.img}
          alt={r.title}
          className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
        />
        <span className={`absolute left-3 top-3 rounded-full px-2.5 py-0.5 text-xs font-medium ${statusStyle[r.status]}`}>
          {r.status}
        </span>
        <span className="absolute right-3 top-3 rounded-full bg-background-950/70 px-2.5 py-0.5 text-xs font-medium text-background-50">
          {r.format}
        </span>
      </div>
      <div className="flex flex-1 flex-col p-4">
        <h3 className="text-sm font-semibold leading-snug text-foreground-950 md:text-base">{r.title}</h3>
        <p className="mt-2 text-xs leading-relaxed text-foreground-600 md:text-sm">{r.desc}</p>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {r.tags.map((t) => (
            <span key={t} className="rounded-full bg-background-100 px-2 py-0.5 text-xs text-foreground-600">
              {t}
            </span>
          ))}
        </div>
        <div className="mt-4 flex items-center justify-between border-t border-background-200/70 pt-3 text-xs text-foreground-500">
          <span>{r.size}</span>
          <span>{r.update}</span>
        </div>
        <button
          onClick={() => onCopy(r.id)}
          className="mt-3 flex h-10 items-center justify-center gap-2 rounded-md bg-primary-500 text-sm font-medium whitespace-nowrap text-background-50 transition-colors hover:bg-primary-600"
        >
          {copiedId === r.id ? (
            <>
              <i className="ri-check-line"></i>
              已复制链接
            </>
          ) : (
            <>
              <i className="ri-link-m"></i>
              获取资源
            </>
          )}
        </button>
      </div>
    </article>
  );
}

export default function HotResources() {
  const [format, setFormat] = useState<(typeof formatTabs)[number]>("全部");
  const [keyword, setKeyword] = useState("");
  const [copiedId, setCopiedId] = useState<number | null>(null);

  const filtered = useMemo(() => {
    return hotResources.filter((r) => {
      const okFormat = format === "全部" || r.format === format;
      const kw = keyword.trim().toLowerCase();
      const okKw = !kw || r.title.toLowerCase().includes(kw) || r.subject.toLowerCase().includes(kw) || r.desc.toLowerCase().includes(kw) || r.tags.some((t) => t.toLowerCase().includes(kw));
      return okFormat && okKw;
    });
  }, [format, keyword]);

  const handleCopy = async (id: number) => {
    const r = hotResources.find((x) => x.id === id);
    if (!r) return;
    try {
      await navigator.clipboard.writeText(`宅宅资源库 · ${r.title}（${r.format}）`);
    } catch {
      /* 剪贴板不可用时静默失败 */
    }
    setCopiedId(id);
    window.setTimeout(() => setCopiedId((v) => (v === id ? null : v)), 1600);
  };

  return (
    <section id="hot" className="scroll-mt-20 bg-background-100/60 py-14 md:py-20">
      <div className="mx-auto max-w-6xl px-4 md:px-6">
        <div className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <span className="text-sm font-medium text-primary-600">热门推荐</span>
            <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">热门资源推荐</h2>
            <p className="mt-2 max-w-lg text-sm text-foreground-600">热门资源提供打包下载入口，切换获取方式即可只看对应格式。</p>
          </div>
          <div className="flex w-full flex-col gap-3 sm:flex-row lg:w-auto">
            <div className="relative sm:w-72">
              <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400"></i>
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索标题 / 分类 / 标签"
                className="h-11 w-full rounded-md border border-background-200 bg-background-50 pl-9 pr-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
              />
            </div>
          </div>
        </div>

        <div className="mb-6 flex flex-wrap gap-2" role="tablist" aria-label="按获取方式筛选">
          {formatTabs.map((f) => (
            <button
              key={f}
              role="tab"
              aria-selected={format === f}
              onClick={() => setFormat(f)}
              className={`flex h-9 items-center rounded-full px-4 text-sm whitespace-nowrap transition-colors ${
                format === f
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-50 text-foreground-600 ring-1 ring-background-200 hover:text-primary-600"
              }`}
            >
              {f}
            </button>
          ))}
          <span className="ml-auto hidden items-center text-xs text-foreground-500 sm:flex">
            共 {filtered.length} 个热门资源
          </span>
        </div>

        {filtered.length > 0 ? (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((r) => (
              <ResourceCard key={r.id} r={r} copiedId={copiedId} onCopy={handleCopy} />
            ))}
          </div>
        ) : (
          <div className="rounded-lg border border-background-200 bg-background-50 px-6 py-14 text-center">
            <i className="ri-inbox-2-line text-4xl text-foreground-300"></i>
            <p className="mt-3 text-sm text-foreground-600">没有找到匹配的资源，换个筛选或关键词试试。</p>
          </div>
        )}
      </div>
    </section>
  );
}